/**
 * Trade Form Screen
 *
 * T062 [US3]: Screen for manually adding a new trade to the portfolio.
 * Handles form validation, trade creation, and navigation.
 * Note: Edit mode (tradeId parameter) is reserved for Phase 6 (Edit Trade).
 * T5.5-033, T5.5-034: Added validation warnings for short sell flags.
 * NOW THEME-AWARE and i18n SUPPORTED
 */

import React, { useState, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  KeyboardAvoidingView,
  Platform,
  TouchableOpacity,
  ActivityIndicator,
} from 'react-native';
import { useNavigation, useRoute, type RouteProp } from '@react-navigation/native';
import { useTranslation } from 'react-i18next';
import type { StackNavigationProp } from '@react-navigation/stack';
import { TradeForm, TradeFormData } from '../../components/portfolio/TradeForm';
import {
  useCreateTrade,
  useUpdateTrade,
  useDeleteTrade,
  useTrade,
} from '../../services/api/queries/useTrades';
import { usePositions } from '../../services/api/queries/usePortfolio';
import { showSuccess, showError, showConfirm } from '../../utils/alert';
import { useTheme } from '../../contexts/ThemeContext';
import type { PortfolioNavigatorParamList } from '../../navigation/PortfolioNavigator';
import type { Position } from '../../services/portfolioApi';

type TradeFormScreenNavigationProp = StackNavigationProp<PortfolioNavigatorParamList, 'AddTrade'>;
type TradeFormScreenRouteProp = RouteProp<PortfolioNavigatorParamList, 'AddTrade'>;

export function TradeFormScreen() {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const styles = useMemo(() => createThemedStyles(theme), [theme]);
  const navigation = useNavigation<TradeFormScreenNavigationProp>();
  const route = useRoute<TradeFormScreenRouteProp>();
  const createTrade = useCreateTrade();
  const updateTrade = useUpdateTrade();
  const deleteTrade = useDeleteTrade();
  const { data: positions } = usePositions();

  // Extract tradeId for edit mode (Phase 6: Edit Trade)
  const tradeId = route.params?.tradeId;
  const isEditMode = !!tradeId;

  // Extract symbol, currency, and instrumentName for pre-filling from position detail
  const symbolParam = route.params?.symbol;
  const currencyParam = route.params?.currency;
  const instrumentNameParam = route.params?.instrumentName;
  // A watchlist item without a resolved instrument carries no currency; the
  // form falls back to USD and flags it instead of defaulting silently.
  const currencyAssumed = !isEditMode && !!symbolParam && !currencyParam;

  // Fetch trade data when editing (Phase 6: Edit Trade)
  const { data: trade, isLoading: isLoadingTrade } = useTrade(
    tradeId!,
    isEditMode // Only fetch when in edit mode
  );

  const [loading, setLoading] = useState(false);

  const handleSubmit = async (data: TradeFormData) => {
    setLoading(true);

    try {
      // Phase 6: Edit mode - update existing trade
      if (isEditMode && tradeId) {
        await updateTrade.mutateAsync({
          tradeId,
          params: {
            symbol: data.symbol,
            trade_datetime: data.trade_datetime,
            quantity: data.quantity,
            trade_price: data.trade_price,
            currency: data.currency,
            transaction_type: data.transaction_type,
            comment: data.comment,
            cost_price: data.cost_price,
          },
        });

        showSuccess(
          `${t('portfolio.trade')} ${t('portfolio.for')} ${data.symbol.toUpperCase()} ${t('portfolio.updatedSuccessfully', { defaultValue: 'updated successfully!' })}`
        );
        navigation.goBack();
        return;
      }

      // Add mode - check if position exists for this symbol
      const symbolUpper = data.symbol.toUpperCase();
      const existingPosition = positions?.find(
        (p: Position) => p.symbol.toUpperCase() === symbolUpper
      );

      // If no existing position, confirm with user before creating
      if (!existingPosition) {
        const confirmed = await showConfirm(
          t('portfolio.newPosition', { defaultValue: 'New Position' }),
          t('portfolio.confirmNewPosition', {
            symbol: symbolUpper,
            defaultValue: `No existing position found for ${symbolUpper}. A new position will be created along with this trade.\n\nDo you want to continue?`,
          }),
          {
            confirmText: t('common.confirm', { defaultValue: 'Confirm' }),
            cancelText: t('common.cancel', { defaultValue: 'Cancel' }),
          }
        );

        if (!confirmed) {
          setLoading(false);
          return;
        }
      }

      // Create new trade (and position if needed)
      await createTrade.mutateAsync({
        symbol: data.symbol,
        trade_datetime: data.trade_datetime,
        quantity: data.quantity,
        trade_price: data.trade_price,
        currency: data.currency,
        transaction_type: data.transaction_type,
        comment: data.comment,
        cost_price: data.cost_price,
      });

      showSuccess(
        `${t('portfolio.trade')} ${t('portfolio.for')} ${symbolUpper} ${t('portfolio.addedSuccessfully', { defaultValue: 'added successfully!' })}`
      );
      navigation.goBack();
    } catch (error: any) {
      showError(
        error.message ||
          `${t('errors.failedToSave', { defaultValue: 'Failed to' })} ${isEditMode ? t('portfolio.update') : t('portfolio.add').toLowerCase()} ${t('portfolio.trade')}`
      );
    } finally {
      setLoading(false);
    }
  };

  // T074 [US4]: Handle delete button with confirmation
  const handleDelete = async () => {
    if (!isEditMode || !tradeId || !trade) return;

    const confirmed = await showConfirm(
      t('portfolio.deleteTrade'),
      `${t('portfolio.sureDeleteTrade', { defaultValue: 'Are you sure you want to delete this' })} ${trade.symbol} ${t('portfolio.trade')}?\n\n${t('portfolio.cannotUndo', { defaultValue: 'This action cannot be undone.' })}`,
      {
        confirmText: t('common.delete'),
        cancelText: t('common.cancel'),
        destructiveAction: true,
      }
    );

    if (confirmed) {
      setLoading(true);
      try {
        await deleteTrade.mutateAsync(tradeId);
        showSuccess(
          `${t('portfolio.trade')} ${t('portfolio.for')} ${trade.symbol.toUpperCase()} ${t('portfolio.deletedSuccessfully', { defaultValue: 'deleted successfully!' })}`
        );
        navigation.goBack();
      } catch (error: any) {
        showError(
          error.message ||
            `${t('errors.failedToSave', { defaultValue: 'Failed to' })} ${t('portfolio.delete').toLowerCase()} ${t('portfolio.trade')}`
        );
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <KeyboardAvoidingView
      style={[styles.container, { backgroundColor: theme.colors.background }]}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
    >
      <ScrollView contentContainerStyle={styles.scrollContent} keyboardShouldPersistTaps="handled">
        {/* Dynamic header based on mode */}
        <View style={styles.header}>
          <View style={styles.headerTitleContainer}>
            <Text style={[styles.title, { color: theme.colors.text }]}>
              {isEditMode ? t('portfolio.editTrade') : t('portfolio.addTrade')}
            </Text>
            <Text style={[styles.subtitle, { color: theme.colors.textSecondary }]}>
              {isEditMode
                ? `${t('portfolio.update')} ${trade?.symbol || t('portfolio.trade').toLowerCase()} ${t('portfolio.details')}`
                : t('portfolio.enterTradeDetails', {
                    defaultValue: 'Enter the details for your new trade',
                  })}
            </Text>
          </View>
        </View>

        {/* Show loading state when fetching trade data */}
        {isLoadingTrade ? (
          <View style={[styles.loadingContainer, { backgroundColor: theme.colors.surface }]}>
            <ActivityIndicator size="large" color={theme.colors.primary} />
            <Text style={[styles.loadingText, { color: theme.colors.textSecondary }]}>
              {t('common.loading')}
            </Text>
          </View>
        ) : (
          /* Form */
          <TradeForm
            onSubmit={handleSubmit}
            onCancel={() => navigation.goBack()}
            onDelete={handleDelete}
            loading={loading}
            initialValues={
              isEditMode && trade
                ? {
                    symbol: trade.symbol,
                    trade_datetime: trade.trade_datetime.split('T')[0], // Extract date from datetime
                    quantity: Math.abs(trade.quantity), // Show positive quantity
                    trade_price: parseFloat(trade.trade_price),
                    currency: trade.currency,
                    transaction_type: trade.transaction_type || 'buy',
                    comment: trade.comment || undefined,
                  }
                : symbolParam
                  ? {
                      symbol: symbolParam,
                      trade_datetime: undefined, // Will default to today
                      quantity: undefined,
                      trade_price: undefined,
                      // Watchlist items may not carry a currency; the form
                      // still needs one so default to USD when absent.
                      currency: currencyParam || 'USD',
                      transaction_type: 'buy',
                      comment: undefined,
                    }
                  : undefined
            }
            symbolReadOnly={!!symbolParam} // Read-only when coming from position detail
            instrumentName={instrumentNameParam}
            currencyAssumed={currencyAssumed}
            submitButtonText={
              isEditMode
                ? `${t('portfolio.update')} ${t('portfolio.trade')}`
                : `${t('portfolio.add')} ${t('portfolio.trade')}`
            }
            showDeleteButton={isEditMode}
          />
        )}
      </ScrollView>
    </KeyboardAvoidingView>
  );
}

/**
 * Create themed styles dynamically
 */
function createThemedStyles(theme: any) {
  return StyleSheet.create({
    container: {
      flex: 1,
    },
    scrollContent: {
      padding: 16,
    },
    header: {
      marginBottom: 24,
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
    },
    headerTitleContainer: {
      flex: 1,
    },
    title: {
      fontSize: theme.typography.fontSize3Xl,
      fontWeight: theme.typography.fontWeightBold,
      marginBottom: 8,
      textTransform: theme.id === 'bauhaus' ? 'uppercase' : 'none',
      letterSpacing: theme.id === 'bauhaus' ? -0.5 : 0,
    },
    subtitle: {
      fontSize: theme.typography.fontSizeMd,
    },
    loadingContainer: {
      borderRadius: theme.radii.lg,
      padding: 32,
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: 200,
      borderWidth: theme.id === 'bauhaus' ? 2 : 0,
      borderColor: theme.colors.border,
    },
    loadingText: {
      marginTop: 16,
      fontSize: theme.typography.fontSizeMd,
    },
  });
}
